import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {Button, Text, Icon} from '@/ui-kits';
import {Routes} from '@/navigation/Routes';
import {Icons, NA, PatientAccountTicketRequestStatus} from '@/constants';
import {formatName, getInitials, formatConsultationDate} from '@/utils/common_functions';
import {useToast} from '@/hooks';
import {useDispatch, useSelector} from 'react-redux';
import {
  getSpecialistDetailsRequest,
  getSpecialistFeedbacksRequest,
  resetPatientDashboardMessage,
  selectHospitalSpecialistRequest,
  specialistBookingRequest,
} from '@/services/redux/slices/patientDashboardSlice';
import {
  specialistDetailsLoading,
  specialistDetailsData,
  patientDashboardFailureState,
  specialistFeedbacksLoading,
  specialistFeedbacksData,
  patientDashboardSuccessState,
  patientDashboardSpecialists,
} from '@/services/redux/selectors/patientDashboardSelector';
import {ErrorCodes, getErrorMessage, getSuccessMessage, SuccessCodes} from '@/utils/getMessages';
import {accountDeletionStatusSelector} from '@/services/redux/selectors/authSelectors';

export const SpecialistProfileView: React.FC = () => {
  // =======================================
  // hooks
  // =======================================
  const navigate = useNavigate();
  const {id} = useParams<{id: string}>();
  const {showToast} = useToast();
  const dispatch = useDispatch();

  // =======================================
  // selectors
  // =======================================
  const isLoading = useSelector(specialistDetailsLoading);
  const doctor = useSelector(specialistDetailsData);
  const failure = useSelector(patientDashboardFailureState) as ErrorCodes;
  const success = useSelector(patientDashboardSuccessState) as SuccessCodes;
  const accountDeletionStatus = useSelector(accountDeletionStatusSelector);
  const feedbacks = useSelector(specialistFeedbacksData);
  const feedbacksLoading = useSelector(specialistFeedbacksLoading);
  const recommendedSpecialistPatientDashboard = useSelector(patientDashboardSpecialists);

  // =======================================
  // states
  // =======================================
  const hasFetchedRef = useRef<number | null>(null);
  const hasFetchedFeedbacksRef = useRef<number | null>(null);
  const failureHandledRef = useRef<string | false>(false);
  const [showAllFeedbacks, setShowAllFeedbacks] = useState(false);
  const isRecommendedSpecialist = useMemo(() => {
    return recommendedSpecialistPatientDashboard.some(specialist => specialist.id === doctor?.id);
  }, [recommendedSpecialistPatientDashboard, doctor]);

  // =======================================
  // side effect
  // =======================================
  useEffect(() => {
    if (!id) {
      showToast('Specialist ID not provided', 'failure');
      navigate(-1);
      return;
    }

    const specialistId = parseInt(id, 10);
    if (isNaN(specialistId)) {
      showToast('Invalid specialist ID', 'failure');
      navigate(-1);
      return;
    }

    // Reset ref when ID changes to allow fetching for new ID
    if (hasFetchedRef.current !== specialistId) {
      hasFetchedRef.current = null;
      failureHandledRef.current = false;
    }

    // Always fetch when ID changes (ref prevents duplicate calls for same ID)
    if (hasFetchedRef.current === null) {
      hasFetchedRef.current = specialistId;
      dispatch(getSpecialistDetailsRequest({id: specialistId}));
    }

    // Fetch feedbacks for this specialist
    if (hasFetchedFeedbacksRef.current !== specialistId) {
      hasFetchedFeedbacksRef.current = specialistId;
      dispatch(getSpecialistFeedbacksRequest({id: specialistId}));
    }
  }, [id, dispatch, navigate, showToast]);

  useEffect(() => {
    if (success) {
      if (['S-10100', 'S-10097'].includes(success)) {
        navigate(Routes.DASHBOARD);
        showToast(getSuccessMessage(success), 'success');
      }
    }
    if (failure && failureHandledRef.current !== failure) {
      failureHandledRef.current = failure;
      showToast(getErrorMessage(failure), 'failure');
    }
    return () => {
      dispatch(resetPatientDashboardMessage());
    };
  }, [failure, success]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Text variant="heading" className="text-ui_blue">
          Loading specialist details...
        </Text>
      </div>
    );
  }

  function handleBookNow() {
    if (!doctor) return;
    if (doctor.is_hospital_specialist) {
      dispatch(selectHospitalSpecialistRequest({id: doctor.id}));
    } else {
      dispatch(specialistBookingRequest({id: doctor.id}));
    }
  }

  // Show error or empty state if no doctor data after loading completes
  if (!doctor && !isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Text variant="heading" className="text-ui_blue">
          No specialist details available
        </Text>
      </div>
    );
  }

  // If still no doctor, return null (shouldn't reach here, but safety check)
  if (!doctor) {
    return null;
  }

  // Safety check: ensure all object properties are properly formatted
  const safeDoctor = {
    ...doctor,
    specialization:
      doctor.specialization && typeof doctor.specialization === 'object'
        ? {id: doctor.specialization.id || 0, label: doctor.specialization.label || ''}
        : {id: 0, label: String(doctor.specialization || '')},
    location:
      typeof doctor.location === 'object' && doctor.location !== null
        ? doctor.location.label || String(doctor.location)
        : typeof doctor.location === 'string'
        ? doctor.location
        : '',
    name: String(doctor.name || ''),
    designation: String(doctor.designation || ''),
    education: String(doctor.education || ''),
    image: String(doctor.image || ''),
  };

  return (
    <div className="gap-10 flex  flex-col p-4 xl:p-11">
      {/* Main compact card */}
      <div className="bg-white rounded-xl border border-ui_blue/6 shadow-[0_8px_30px_rgba(39,71,96,0.06)] flex flex-col 3xl:flex-row items-stretch overflow-visible mt-4 sm:mt-6 w-full max-w-full">
        {/* Image panel - first on mobile, right side on desktop */}
        <div className="w-full 3xl:w-[280px] 4xl:w-[300px] flex-shrink-0 order-1 3xl:order-2">
          <div className="relative w-full max-w-[220px] md:max-w-[240px] lg:max-w-[260px] xl:max-w-[280px] 2xl:max-w-[300px] mx-auto 3xl:max-w-none 3xl:mx-0 h-[200px] md:h-[240px] lg:h-[260px] xl:h-[280px] 2xl:h-[300px] 3xl:h-full 3xl:min-h-[170px] 4xl:min-h-[200px] bg-profile_bg_pale_blue flex items-end justify-center overflow-visible">
            {safeDoctor.image && safeDoctor.image.trim() !== '' ? (
              <img
                src={safeDoctor.image}
                alt={safeDoctor.name}
                className="h-[230px] md:h-[280px] lg:h-[320px] xl:h-[360px] 2xl:h-[350px] 3xl:h-[340px] 4xl:h-[380px] w-auto object-contain object-bottom -mt-5 md:-mt-7 lg:-mt-8 xl:-mt-10 2xl:-mt-10 3xl:-mt-12 4xl:-mt-14 drop-shadow-[0_10px_25px_rgba(0,0,0,0.15)]"
              />
            ) : (
              <div className="flex items-center justify-center w-full h-full">
                <Text variant="free" className="text-ui_blue text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-FigtreeBold">
                  {getInitials(safeDoctor.name)}
                </Text>
              </div>
            )}
          </div>
        </div>

        {/* Content - second on mobile, left side on desktop */}
        <div className="flex-1 flex flex-col min-w-0 px-5 pt-4 pb-3 sm:px-6 sm:pt-5 sm:pb-4 2xl:px-6 2xl:pt-4 2xl:pb-4 3xl:px-6 3xl:pt-3 3xl:pb-1 4xl:px-7 4xl:pt-4 4xl:pb-1 order-2 3xl:order-1">
          {/* Name and Rating in single line */}
          <div className="flex flex-col 3xl:flex-row items-center 3xl:items-center justify-center 3xl:justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0 text-center 3xl:text-left">
              <Text
                variant="heading"
                className="font-FigtreeBold text-ui_blue text-xl sm:text-2xl md:text-4xl lg:text-4xl xl:text-4xl 2xl:text-3xl 3xl:text-3xl break-words">
                Dr. {formatName(safeDoctor.name)}
              </Text>
            </div>

            {/* Rating badge */}
            {safeDoctor.rating ? (
              <div className="inline-flex items-center justify-center gap-2 bg-profile_rating_bg px-3 py-1 rounded-full flex-shrink-0">
                <span className="text-profile_rating_yellow text-lg sm:text-xl leading-none flex items-center justify-center">★</span>
                <span className="font-FigtreeBold text-ui_blue text-md">{safeDoctor.rating ? `${Number(safeDoctor.rating).toFixed(1)}/5` : `*/5`}</span>
              </div>
            ) : null}
          </div>

          {/* All info fields stacked vertically on mobile, two-column on desktop */}
          {/* <div className="mt-6 sm:mt-8 md:mt-10 lg:mt-12 2xl:mt-8 3xl:mt-6 4xl:mt-8 flex flex-col 3xl:grid 3xl:grid-cols-2 3xl:items-start 3xl:auto-rows-auto gap-4 2xl:gap-4 3xl:gap-x-8 3xl:gap-y-6 4xl:gap-x-10 4xl:gap-y-7 5xl:gap-x-20 text-sm sm:text-base md:text-lg lg:text-lg xl:text-lg 2xl:text-base"> */}
          {/* Left column */}
          {/* <div className="flex flex-col gap-y-4 2xl:gap-y-4 3xl:gap-y-6 4xl:gap-y-7 flex-1 min-w-0 3xl:min-w-[200px] 4xl:min-w-[220px] 3xl:contents">
              <div className="flex flex-row items-start gap-2 min-w-0 3xl:min-h-[32px] 3xl:col-start-1 3xl:row-start-1">
                <span className="font-FigtreeRegular text-ui_input_border_dark whitespace-nowrap flex-shrink-0">Specialization :</span>
                <span className="font-FigtreeSemiBold text-ui_blue break-words min-w-0 flex-1">
                  {safeDoctor.specialization.label || NA}
                </span>
              </div>
              <div className="flex  flex-row items-start gap-2 min-w-0 3xl:min-h-[32px] 3xl:col-start-1 3xl:row-start-2">
                <span className="font-FigtreeRegular text-ui_input_border_dark whitespace-nowrap flex-shrink-0">Current Designation :</span>
                <span className="font-FigtreeSemiBold text-ui_blue break-words min-w-0 flex-1">{formatName(safeDoctor.designation) || NA}</span>
              </div>
              <div className="flex flex-row items-start gap-2 min-w-0 3xl:col-start-1 3xl:row-start-3">
                <span className="font-FigtreeRegular text-ui_input_border_dark whitespace-nowrap flex-shrink-0">Education :</span>
                <span className="font-FigtreeSemiBold text-ui_blue break-words min-w-0 flex-1">{formatName(safeDoctor.education) || NA}</span>
              </div>
              <div className="flex flex-row items-start gap-2 min-w-0 3xl:min-h-[32px] 3xl:col-start-1 3xl:row-start-4">
                <span className="font-FigtreeRegular text-ui_input_border_dark whitespace-nowrap flex-shrink-0">Location :</span>
                <span className="font-FigtreeSemiBold text-ui_blue break-words min-w-0 flex-1">{safeDoctor.location || NA}</span>
              </div>
            </div> */}

          {/* Right column */}
          {/* <div className="flex flex-col gap-y-4 2xl:gap-y-4 3xl:gap-y-6 4xl:gap-y-7 flex-1 min-w-0 3xl:min-w-[180px] 4xl:min-w-[200px] 5xl:min-w-[220px] 3xl:contents">
              <div className="flex flex-row items-center gap-2 min-w-0 3xl:min-h-[32px] 3xl:col-start-2 3xl:row-start-1 debug">
                <span className="font-FigtreeRegular text-ui_input_border_dark whitespace-nowrap flex-shrink-0">Consultation Fee :</span>
                <span className={safeDoctor.price ? "font-FigtreeBold text-ui_blue text-lg sm:text-2xl md:text-3xl lg:text-3xl xl:text-3xl 2xl:text-2xl" : "font-FigtreeSemiBold text-ui_blue break-words min-w-0 flex-1"}>₹ {safeDoctor.price ? Number(safeDoctor.price).toLocaleString() : NA}</span>
              </div>
              <div className="flex items-center justify-center 3xl:justify-start min-w-0 3xl:h-auto mt-6 sm:mt-8 md:mt-10 2xl:mt-8 3xl:mt-1 4xl:mt-1 3xl:col-start-2 3xl:row-start-3">
                {accountDeletionStatus !== PatientAccountTicketRequestStatus.Approved && !isRecommendedSpecialist && (
                  <Button
                    title="Book Now"
                    variant="primary"
                    onClick={handleBookNow}
                    className="mb-2 3xl:mb-0 px-4 min-[425px]:px-5 sm:px-8 py-2 rounded-xl text-xl font-FigtreeSemiBold shadow-sm w-full min-[425px]:w-auto md:w-auto lg:w-auto xl:w-auto 2xl:w-auto min-w-[120px] min-[425px]:min-w-[130px] sm:min-w-[280px] md:min-w-[180px] lg:min-w-[180px] xl:min-w-[180px] 2xl:min-w-[120px] 4xl:min-w-[130px] 5xl:min-w-[150px]"
                  />
                )}
              </div>
            </div> */}
          {/* </div> */}
          <div
            className="
              grid
              grid-cols-1
              md:grid-cols-[1.2fr_0.8fr]
              min-[1280px]:grid-cols-[1.1fr_0.9fr]
              min-[1335px]:grid-cols-[1.2fr_0.8fr]
              gap-4 my-4
            ">
            {' '}
            <div className="flex flex-col justify-between shrink-0 gap-4">
              <ProfileDetails label="Specialization" value={safeDoctor.specialization?.label || NA} />
              <ProfileDetails label="Current Designation" value={safeDoctor.designation || NA} />
              <ProfileDetails label="Education" value={safeDoctor.education || NA} />
              {safeDoctor.is_hospital_specialist ? (
                <ProfileDetails label="Location" value={`${safeDoctor?.hospital_name}, ${safeDoctor?.hospital_address}` || NA} />
              ) : (
                <ProfileDetails label="Location" value={safeDoctor.location || NA} />
              )}
            </div>
            <div className="flex flex-col shrink-0">
              <ProfileDetails
                className="items-center"
                label="Consultation Fee"
                value={`₹ ${safeDoctor.price || NA}`}
                textClassName="text-[24px] 2xl:text-[26px] 3xl:text-[28px] break-normal!"
              />
              {accountDeletionStatus !== PatientAccountTicketRequestStatus.Approved && !isRecommendedSpecialist && (
                <Button
                  title="Book Now"
                  variant="primary"
                  onClick={handleBookNow}
                  className="mb-2 3xl:mb-0 px-4 min-[425px]:px-5 sm:px-8 py-2 rounded-xl text-xl mt-auto font-FigtreeSemiBold shadow-sm w-full min-[425px]:w-auto md:w-auto lg:w-auto xl:w-auto 2xl:w-auto min-w-[120px] min-[425px]:min-w-[130px] sm:min-w-[280px] md:min-w-[180px] lg:min-w-[180px] xl:min-w-[180px] 2xl:min-w-[120px] 4xl:min-w-[130px] 5xl:min-w-[150px]"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Professional Experience and Qualifications in single outer div */}
      <div className="bg-white rounded-xl shadow-[0px_4px_41.6px_-13px_rgba(0,0,0,0.15)] p-3 sm:p-6">
        <div className="flex flex-col gap-4 sm:gap-6">
          {/* Professional Experience */}
          <div className="bg-ui_bg_sky rounded-lg p-3 sm:p-6 border border-border">
            <div className="flex items-center gap-2 mb-4 sm:mb-6 flex-wrap">
              <Icon type="business_center_logo" className="w-5 h-5 sm:w-6 sm:h-6" />
              <div className="text-ui_blue text-lg sm:text-2xl ml-1 sm:ml-2">
                <span className="font-FigtreeBold">Professional Experience</span>
                <span className="font-FigtreeRegular inline min-[375px]:block sm:inline"> ({safeDoctor.experience ?? 0} Years)</span>
              </div>
            </div>
            <Text variant="free" className="font-FigtreeMeduim text-ui_blue text-base ml-0 sm:ml-9">
              {String((doctor as any).professional_experience || NA)}
            </Text>
          </div>

          {/* Qualifications */}
          <div className="bg-ui_bg_sky rounded-lg p-3 sm:p-6 border border-border">
            <div className="flex items-center gap-2 mb-4 sm:mb-6">
              <Icon type="graduation_icon" className="w-5 h-5 sm:w-6 sm:h-6" />
              <Text variant="free" className="text-ui_blue font-FigtreeBold text-lg sm:text-2xl ml-1 sm:ml-2">
                Qualifications
              </Text>
            </div>
            <Text variant="free" className="font-FigtreeMeduim text-ui_blue text-base ml-0 sm:ml-9">
              {String((doctor as any).qualification || NA)}
            </Text>
          </div>
        </div>
      </div>

      {/* remaining grid sections unchanged */}
      <div className="grid grid-cols-1 2xl:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white rounded-xl border border-ui_blue/15 shadow-[0_8px_30px_rgba(39,71,96,0.06)] p-3 sm:p-6">
          <div className="flex items-center gap-2 mb-4 sm:mb-6">
            <Icon type="yellow_award_icon" className="w-5 h-5 sm:w-6 sm:h-6" />
            <Text variant="free" className="text-ui_blue font-FigtreeBold text-lg sm:text-2xl 2xl:text-xl 4xl:text-2xl ml-2 lg:whitespace-nowrap">
              Awards & Achievements
            </Text>
          </div>
          <div className="flex flex-col gap-2">
            {(() => {
              const awards = (doctor as any)?.awards_and_achievements || (doctor as any)?.awards_achievements;
              const awardsList =
                typeof awards === 'string'
                  ? (() => {
                      try {
                        const parsed = JSON.parse(awards);
                        return Array.isArray(parsed) ? parsed : [];
                      } catch {
                        return [];
                      }
                    })()
                  : Array.isArray(awards)
                  ? awards
                  : [];

              if (awardsList.length === 0) {
                return (
                  <Text variant="free" className="font-FigtreeMeduim text-ui_blue text-base ml-4">
                    {NA}
                  </Text>
                );
              }

              // Convert awards to strings (handle both string and object formats)
              const awardsToShow = awardsList.map((award: any) => {
                if (typeof award === 'string') return award;
                if (typeof award === 'object' && award !== null) {
                  return award.label || award.name || award.text || String(award);
                }
                return String(award);
              });

              return awardsToShow.map((award: string, index: number) => (
                <div key={index} className="flex items-start gap-2">
                  <span className="text-ui_blue flex-shrink-0 mt-0.5">•</span>
                  <Text variant="free" className="font-FigtreeRegular text-ui_blue text-base leading-6">
                    {award}
                  </Text>
                </div>
              ));
            })()}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-ui_blue/15 shadow-[0_8px_30px_rgba(39,71,96,0.06)] p-3 sm:p-6">
          <div className="flex items-center gap-2 mb-4 sm:mb-6">
            <Icon type="stethoscope2" className="w-5 h-5 sm:w-6 sm:h-6" />
            <Text variant="free" className="text-ui_blue font-FigtreeBold text-lg sm:text-2xl 2xl:text-xl 4xl:text-2xl ml-2 lg:whitespace-nowrap">
              Areas of Expertise
            </Text>
          </div>
          <div className="flex flex-col gap-2">
            {(() => {
              const expertise = (doctor as any)?.areas_of_expertise;
              const expertiseList =
                typeof expertise === 'string'
                  ? (() => {
                      try {
                        const parsed = JSON.parse(expertise);
                        return Array.isArray(parsed) ? parsed : [];
                      } catch {
                        return [];
                      }
                    })()
                  : Array.isArray(expertise)
                  ? expertise
                  : [];

              if (expertiseList.length === 0) {
                return (
                  <Text variant="free" className="font-FigtreeMeduim text-ui_blue text-base ml-4">
                    {NA}
                  </Text>
                );
              }

              // Convert items to strings (handle both string and object formats)
              const itemsToShow = expertiseList.map((item: any) => {
                if (typeof item === 'string') return item;
                if (typeof item === 'object' && item !== null) {
                  return item.label || item.name || String(item);
                }
                return String(item);
              });

              return (
                <div className="flex flex-wrap gap-2">
                  {itemsToShow.map((item: string, index: number) => (
                    <div key={index} className="px-3 py-1.5 rounded-lg border border-ui_grey bg-ui_bg_sky">
                      <Text variant="free" className="font-FigtreeMeduim text-ui_blue text-base">
                        {item}
                      </Text>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Patient Feedbacks Section */}
      {(() => {
        if (feedbacksLoading || feedbacks.length === 0) return null;

        const displayedFeedbacks = showAllFeedbacks ? feedbacks : feedbacks.slice(0, 2);

        return (
          <div className="bg-profile_feedback_bg rounded-xl border border-ui_blue/15 shadow-[0px_4px_41.6px_-13px_rgba(0,0,0,0.15)] p-3 sm:p-6">
            <div className="flex items-center gap-2 mb-4 sm:mb-6">
              <Icon type="patient_feedback_icon" className="w-8 h-8 sm:w-7 sm:h-7 lg:w-10 lg:h-10" />
              <Text variant="free" className="text-ui_blue font-FigtreeBold text-lg sm:text-2xl">
                Patient Feedbacks
              </Text>
            </div>

            <div className="grid grid-cols-1 2xl:grid-cols-2 gap-4 sm:gap-6">
              {displayedFeedbacks.map((feedback: {id: number; patient_name: string; created_at: string; message: string; rating: number}) => (
                <div key={feedback.id} className="bg-white rounded-3xl border border-ui_blue/15 shadow-[0_8px_30px_rgba(39,71,96,0.06)] p-4 sm:p-6 relative">
                  {/* Quotation mark icon */}
                  <div className="absolute top-4 left-6">
                    <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-ui_blue/10 flex items-center justify-center">
                      <img src={Icons.double_quote_icon} alt="quote-icon" className="w-5 h-5 sm:w-6 sm:h-6 object-contain" />
                    </div>
                  </div>

                  {/* Feedback content */}
                  <div className="relative z-10 pt-6 mt-6 ml-2">
                    <Text variant="free" className="font-FigtreeRegular text-ui_blue text-sm sm:text-base leading-relaxed mb-4">
                      {feedback.message}
                    </Text>

                    <div className="flex flex-col 4xl:flex-row 4xl:items-center 4xl:justify-between gap-2 mt-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Text variant="free" className="font-FigtreeBold text-ui_blue text-sm sm:text-base">
                          {feedback.patient_name}
                        </Text>
                        {feedback.created_at && (
                          <>
                            <Text variant="free" className="font-FigtreeRegular text-ui_input_border_dark text-xs sm:text-sm ml-2">
                              {formatConsultationDate(feedback.created_at, 'short', '-')}
                            </Text>
                          </>
                        )}
                      </div>

                      {/* Rating stars */}
                      <div className="flex items-center gap-1">
                        {Array.from({length: Math.round(feedback.rating || 0)}).map((_, index) => (
                          <img key={index} src={Icons.feedback_star} alt="star" className="w-4 h-4 sm:w-5 sm:h-5 object-contain" />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {feedbacks.length > 2 && (
              <div className="text-center mt-4 sm:mt-6">
                <button
                  onClick={() => setShowAllFeedbacks(!showAllFeedbacks)}
                  className="text-ui_blue/70 font-FigtreeSemiBold text-base sm:text-lg hover:opacity-80 transition-all">
                  {showAllFeedbacks ? 'See less' : 'See more'}
                </button>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

interface ProfileDetailsProps {
  label: string;
  value: string | JSX.Element;
  textClassName?: string;
  className?: string;
}
function ProfileDetails(props: ProfileDetailsProps) {
  const {label, value, textClassName, className} = props;
  return (
    <div className={`flex flex-row items-start gap-2 min-w-0 ${className || ''}`}>
      <Text
        variant="free"
        className="!font-FigtreeRegular text-ui_input_border_dark whitespace-nowrap flex-shrink-0 text-[16px] 2xl:text-[18px] 3xl:text-[20px]">
        {label} :
      </Text>
      <Text
        variant="free"
        className={`!font-FigtreeSemiBold text-ui_blue break-words min-w-0 flex-1 text-[16px] 2xl:text-[18px] 3xl:text-[20px] ${textClassName || ''}`}>
        {value || NA}
      </Text>
    </div>
  );
}
